import React, { useEffect, useState, useContext } from 'react';
import { Box, Container, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert, useTheme, Tooltip, Card } from '@mui/material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BusinessIcon from '@mui/icons-material/Business';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentsIcon from '@mui/icons-material/Payments';
import TimelapseIcon from '@mui/icons-material/Timelapse';
import { BusinessContext } from '../context/BusinessContext'; // To potentially get currency symbols
import currencies from '../constants/currencies.json';

const API_URL = process.env.REACT_APP_API_URL;

// Helper function to format currency (symbol only, like Businesses.js)
const formatCurrency = (amount, currencyCode) => {
  if (amount === null || amount === undefined || !currencyCode) return 'N/A';
  const currencyData = currencies[currencyCode.toUpperCase()];
  const symbol = currencyData ? currencyData.symbol_native : '';
  // const name = currencyData ? currencyData.name : currencyCode; // Remove name lookup
  return `${symbol}${Number(amount).toLocaleString()}`; // Keep only symbol + amount
};

// Helper to calculate trial remaining days
const getTrialRemaining = (trialEndsAt) => {
  if (trialEndsAt === null || trialEndsAt === undefined) return "Not Set"; // Explicitly handle null/undefined

  const endDate = new Date(trialEndsAt);
  // Check if endDate is a valid date after conversion
  if (isNaN(endDate.getTime())) return "Invalid Date";

  const now = new Date();

  if (endDate <= now) return "Ended";

  // Calculate the difference in milliseconds
  const diffTime = endDate.getTime() - now.getTime();

  // Calculate the difference in days, rounding up
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`;
};

// Update renderBusinessStatus to return elements matching Businesses.js exactly
const renderBusinessStatus = (status, isMonitored, theme) => { // Pass theme for colors
  if (status === 'pending') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <TimelapseIcon sx={{ // Changed icon to Timelapse to match previous implementation
          fontSize: '1rem',
          mr: 0.5,
          color: '#F57C00', // Match Businesses.js color
          // Potentially add animation if needed, like in Businesses.js
        }} />
        <Typography variant="body2" sx={{ fontWeight: 500, color: '#F57C00' }}>
          Processing
        </Typography>
      </Box>
    );
  } else if (isMonitored) {
    return (
      <Tooltip title="Monitored">
        {/* Use Typography + Icon instead of IconButton to match non-interactive display */}
        <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.success.dark }}>
          <CheckCircleOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>Monitored</Typography>
        </Box>
      </Tooltip>
    );
  } else {
    return (
      <Tooltip title="Not Monitored">
        <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic' }}>
          Not Monitored
        </Typography>
      </Tooltip>
    );
  }
};

// Helper function to format optional dates
const formatOptionalDate = (dateString) => {
  if (!dateString) return 'Never'; // Display 'Never' if null/undefined
  try {
    return new Date(dateString).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch (e) {
    return 'Invalid Date';
  }
};

const AdminPage = () => {
  const [usersData, setUsersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const theme = useTheme();
  const { currencySymbol } = useContext(BusinessContext); // Use context for default symbol if needed

  useEffect(() => {
    const fetchAdminData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetch(`${API_URL}/admin/users`, { credentials: 'include' });
        if (!response.ok) {
          if (response.status === 403) {
            throw new Error('Access Denied: Administrator privileges required.');
          } else {
            const errorData = await response.json();
            throw new Error(errorData.detail || `Failed to fetch admin data (Status: ${response.status})`);
          }
        }
        const data = await response.json();
        setUsersData(data.users || []);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError(err.message || 'An error occurred while loading admin data.');
      } finally {
        setLoading(false);
      }
    };

    fetchAdminData();
  }, []);

  const renderStatus = (status, trialEndsAt) => {
    const trialRemaining = getTrialRemaining(trialEndsAt);

    switch (status) {
      case 'trialing':
        let trialText = 'Trial';
        let trialColor = theme.palette.warning.dark;
        if (trialRemaining === "Not Set") {
          trialText = 'Trial (null)';
          trialColor = theme.palette.error.dark;
        } else if (trialRemaining === "Ended") {
          trialText = 'Trial (Ended)';
          trialColor = theme.palette.error.dark;
        } else if (trialRemaining === "Invalid Date") {
          trialText = 'Trial (Invalid Date)';
          trialColor = theme.palette.error.dark;
        } else {
          trialText = `Trial (${trialRemaining})`;
        }
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: trialColor }}>
            <TimelapseIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {trialText}
          </Box>
        );
      case 'active':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.success.dark }}>
            <CheckCircleOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            Subscribed
          </Box>
        );
      case 'canceled':
      case 'past_due':
      case 'unpaid':
      case 'incomplete':
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.error.dark }}>
            <HighlightOffIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Box>
        );
      default:
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', color: theme.palette.grey[600] }}>
            <HelpOutlineIcon sx={{ fontSize: '1rem', mr: 0.5 }} />
            {status || 'Unknown'}
          </Box>
        );
    }
  };

  return (
    <Box sx={{
      minHeight: '100%',
      background: 'linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%)',
      display: 'flex',
      flexDirection: 'column',
      px: { xs: 2, lg: 4 },
      py: { xs: 2, lg: 4 },
      opacity: 0,
      animation: 'fadeIn 1s forwards',
      '@keyframes fadeIn': {
        from: { opacity: 0 },
        to: { opacity: 1 },
      }
    }}>
      <Container maxWidth="xl" sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ mb: { xs: 2, lg: 4 }, display: 'flex', alignItems: 'center' }}>
          <AdminPanelSettingsIcon sx={{ fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#1976d2', mr: 1 }} />
          <Typography variant="h3" component="h1" sx={{ fontWeight: 800, fontSize: { xs: '1.75rem', lg: '2.5rem' }, color: '#0A2540' }}>
            Admin Overview
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        <Card sx={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)',
          borderRadius: { xs: '12px', sm: '16px', md: '24px' },
          border: '1px solid rgba(25, 118, 210, 0.1)',
          boxShadow: '0 20px 40px -12px rgba(0,0,0,0.1)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #1976d2, #42a5f5)'
          }
        }}>
          <TableContainer sx={{ margin: 0, padding: { xs: 1, md: 2 } }}>
            <Table
              size="medium"
              aria-label="admin users table"
              sx={{
                tableLayout: 'fixed', // Use fixed layout for consistent column widths
                '& th, & td': {
                  px: { xs: 1, sm: 2 },
                  py: { xs: 1.5, sm: 2 },
                  fontSize: { xs: '0.875rem', md: '1rem' }, // Responsive font size
                  borderBottom: '1px solid rgba(25, 118, 210, 0.08)', // Consistent border
                  lineHeight: 1.4, // Improved line height for readability
                  verticalAlign: 'top' // Align content to top for consistency when businesses list wraps
                },
                '& th': {
                  fontWeight: 700,
                  color: '#0A2540',
                  borderBottom: '2px solid rgba(25, 118, 210, 0.1)', // Thicker bottom border for header
                },
                '& tr:last-child td': { // Remove border from last row
                  borderBottom: 0,
                }
              }}
            >
              <TableHead>
                <TableRow>
                  {/* Adjust column widths - make email slightly narrower, add last login */}
                  <TableCell sx={{ width: { xs: '25%', md: '20%' } }}>User Email</TableCell>
                  <TableCell sx={{ width: { xs: '20%', md: '18%' } }}>Signup Date</TableCell>
                  <TableCell sx={{ width: { xs: '20%', md: '18%' } }}>Last Login</TableCell> {/* New Column */}
                  <TableCell sx={{ width: { xs: '15%', md: '12%' } }}>Status</TableCell>
                  <TableCell sx={{ width: { xs: '20%', md: '32%' } }}>Businesses</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    {/* Spanning all 5 columns now */}
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={40} thickness={4} sx={{ color: '#1976d2' }} />
                    </TableCell>
                  </TableRow>
                ) : usersData.length === 0 && !error ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography sx={{ fontSize: '0.95rem', color: '#666666' }}>
                        No user data found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  usersData.map((user) => (
                    <TableRow
                      key={user.id}
                      sx={{
                        transition: 'background-color 0.2s ease-in-out',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.04)'
                        },
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ fontWeight: 500, color: '#0A2540' }}>
                        {user.email}
                        {user.role === 'admin' && (
                          <Tooltip title="Administrator">
                            <AdminPanelSettingsIcon sx={{ fontSize: '1.1rem', ml: 0.5, verticalAlign: 'middle', color: theme.palette.primary.main }} />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#546E7A' }}>
                        {formatOptionalDate(user.created_at)}
                      </TableCell>
                      <TableCell sx={{ color: '#546E7A' }}>
                        {formatOptionalDate(user.last_login_at)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 500 }}>
                        {renderStatus(user.subscription_status, user.trial_ends_at)}
                      </TableCell>
                      <TableCell>
                        {user.businesses.length === 0 ? (
                          <Typography variant="body2" sx={{ color: '#78909C', fontStyle: 'italic' }}>
                            No businesses added
                          </Typography>
                        ) : (
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            {user.businesses.map((biz) => (
                              <Paper key={biz.id} elevation={0} sx={{
                                p: 1.5,
                                borderRadius: '8px', // Slightly more rounded corners
                                border: `1px solid ${theme.palette.divider}`,
                                background: 'rgba(25, 118, 210, 0.02)' // Subtle background
                              }}>
                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#0A2540', mb: 0.75 }}>
                                  {biz.name}
                                </Typography>
                                {/* Add Address */}
                                <Typography variant="caption" sx={{ color: theme.palette.grey[700], display: 'block', mb: 1 }}>
                                  {biz.address}
                                </Typography>
                                <Typography variant="caption" sx={{ color: theme.palette.grey[600], display: 'block', mb: 0.25 }}>
                                  Type: {biz.primary_type || 'N/A'}
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                  {/* Revenue Info - Apply Businesses.js styling */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {/* Render formatted amount */}
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: '#1976d2', // Match Businesses.js color
                                        fontWeight: 600,    // Match Businesses.js weight
                                        fontSize: 'inherit' // Inherit font size from parent cell/paper
                                      }}>
                                      {formatCurrency(biz.turnover, biz.currency)}
                                      {/* Render currency code in a separate span with grey, non-bold style */}
                                      <Box component="span" sx={{
                                        fontWeight: 'normal',
                                        color: theme.palette.grey[600],
                                        ml: 0.5 // Add a small space before the code
                                      }}>
                                        ({biz.currency || 'N/A'})
                                      </Box>
                                    </Typography>
                                  </Box>
                                  {/* Monitoring Status - Use updated renderBusinessStatus */}
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    {renderBusinessStatus(biz.status, biz.is_monitored, theme)}
                                  </Box>
                                </Box>
                              </Paper>
                            ))}
                          </Box>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

      </Container>
    </Box>
  );
};

export default AdminPage; 